import { useEffect, useState } from 'react'
import { company, customerData, ValidiFiBankVerification } from '../../../utilities/CustomerData'
import { useNavigate } from 'react-router-dom'
import { apiURL } from '../../../utilities/URLs'
import { refreshAccessToken } from '../../../utilities/extensions'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { Info } from 'lucide-react'

export function AdvancePaymentBankVerification({ step, setStep, setReload, handleInitApplication }) {
  const navigate = useNavigate()
  const [bankStep, setBankStep] = useState(0)
  const [validiFiBankVerification, setValidiFiBankVerification] = useState(new ValidiFiBankVerification())
  const [sendingValidation, setSendingValidation] = useState(false)
  const [showError, setShowError] = useState(false)
  const [autocomplete, setAutocomplete] = useState(null)
  const [showToolTip, setShowToolTip] = useState(false)
  const [estimatedDate, setEstimatedDate] = useState(new Date())

  const libraries = ['places']

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setValidiFiBankVerification((prevVerification) => ({
        ...prevVerification,
        bankCity: getAddressComponent('locality'),
        bankState: getAddressComponent('administrative_area_level_1')
      }))
    }
  }

  const handleSendBankInfo = async () => {
    setSendingValidation(true)

    const form = new FormData()
    form.append('bankInfo', JSON.stringify({
      accountNumber: validiFiBankVerification.accountNumber,
      routingNumber: validiFiBankVerification.routingNumber,
      firstName: customerData.customerLeadData.leadContact.contactFirstName,
      lastName: customerData.customerLeadData.leadContact.contactLastName,
      email: customerData.customerLeadData.leadContact.contactEmail,
      phone: customerData.customerLeadData.leadContact.contactPhone.replace('+1', '')
    }))

    const bankValidationResponse = await fetch(`${apiURL}/api/requestBankValidation`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    
    const bankValidationData = await bankValidationResponse.json()
    setSendingValidation(false)

    if(bankValidationData.response.error) {
      if(bankValidationData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleSendBankInfo() : navigate('/', { state: { expired: true } })
      } else {
        console.error(bankValidationData.response.error)
        setShowError(true)
        setTimeout(() => {
          setShowError(false)
        }, 4000)
      }
    } else {
      handleInitApplication(new ValidiFiBankVerification({
        customerId: customerData.customerLeadData.leadContact.contactId,
        requestId: bankValidationData.response.requestId,
        transferType: bankValidationData.response.transferType,
        bankName: validiFiBankVerification.bankName,
        accountType: validiFiBankVerification.accountType,
        accountNumber: validiFiBankVerification.accountNumber,
        routingNumber: validiFiBankVerification.routingNumber,
        bankCity: validiFiBankVerification.bankCity,
        bankState: validiFiBankVerification.bankState,
        accessUrl: bankValidationData.response.authUrl,
        estimatedDepositDate: bankValidationData.response.estimatedDepositDate
      }))
      setEstimatedDate(new Date(bankValidationData.response.estimatedDepositDate))
      setBankStep(bankStep + 1) 
    }
  }

  useEffect(() => {
    window.scrollTo(0,0)
    document.title = `${company.companyName} | Bank Verification`
  }, [])

  return (
    <div className='lg:max-w-screen-lg w-11/12 flex flex-col items-center lg:p-10'>
      {isLoaded ? (
        bankStep === 0 ? (
          <div className='w-full flex flex-col gap-4'>
            <p className='text-2xl font-bold'>Connect Your Bank Account</p>
            <p className='font-bold'>Great News! Your Application is being Processed</p>
            <p>We are pleased to inform you that your application is now being processed. To continue with your filing, we kindly ask that you provide a method of payment to continue. This step ensures that your funds are securely routed to your account.</p>
            <ul className='list-disc ml-5 mt-2'>
              <li>Provide your bank account details below - we'll use an ACH transfer to deposit funds directly into your bank account.</li>
              <li>We've partnered with ValidiFi, a trusted partner for secure account verification, to ensure your account's security at every step.</li>
              <li>ValidiFi will make tiny deposits (usually less than $1) to verify your account.</li>
              <li>You'll get simple instructions on how to confirm these deposits.</li>
            </ul>
            <p>Ready to set up your payday? Let's go!</p>
            <div className='w-full flex flex-col gap-4'>
              <p className='font-bold text-xl'>Bank Account Details</p>
              <div className='flex flex-col'>
                <label htmlFor='email' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Institution Name*
                </label>
                <input
                  type='text'
                  id='email'
                  placeholder='Institution Name'
                  className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={validiFiBankVerification.bankName}
                  onChange={(e) => setValidiFiBankVerification((prevVerification) => ({
                    ...prevVerification,
                    bankName: e.target.value
                  }))}
                  required
                />
              </div>
              <div className='flex flex-col'>
                <div className='flex gap-2'>
                  <label htmlFor='email' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                    Institution City*
                  </label>
                  <div className='relative'>
                  <Info
                    className='w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700'
                    onMouseEnter={() => setShowToolTip(true)}
                    onMouseLeave={() => setShowToolTip(false)}
                  />
                  {showToolTip && (
                    <div className='absolute right-0 w-64 p-2 mt-2 text-sm text-white bg-gray-800 rounded shadow-lg'>
                      The city of your bank's local branch closest to you.
                    </div>
                  )}
                  </div>
                </div>
                <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                  <input
                    type='text'
                    id='email'
                    placeholder='Institution City'
                    className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                    value={validiFiBankVerification.bankCity}
                    onChange={(e) => setValidiFiBankVerification((prevVerification) => ({
                      ...prevVerification,
                      bankCity: e.target.value
                    }))}
                    required
                  />
                </Autocomplete>
              </div>
              <div className='flex flex-col'>
                <label htmlFor='email' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Institution State*
                </label>
                <input
                  type='text'
                  id='email'
                  placeholder='Institution State'
                  className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={validiFiBankVerification.bankState}
                  onChange={(e) => setValidiFiBankVerification((prevVerification) => ({
                    ...prevVerification,
                    bankState: e.target.value
                  }))}
                  required
                />
              </div>
              <div>
                <label htmlFor='courier' className='block text-gray-700 font-bold'>Select Bank Type</label>
                <select
                  id='courier'
                  value={validiFiBankVerification.accountType}
                  onChange={(e) => setValidiFiBankVerification((prevVerification) => ({
                    ...prevVerification,
                    accountType: e.target.value
                  }))}
                  className={`w-full border rounded-lg px-4 py-2 transition-colors duration-200 ease-in-out shadow-sm focus:outline-none focus:ring-2 bg-white text-gray-900 border-gray-300 focus:ring-blue-500`}
                >
                  <option value='' disabled>Select a Bank Account Type</option>
                  <option value='CHECKING'>CHECKING</option>
                  <option value='SAVINGS'>SAVINGS</option>
                </select>
              </div>
              <div className='flex flex-col'>
                <label htmlFor='email' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Account Number*
                </label>
                <input
                  type='text'
                  id='email'
                  placeholder='XXXXXXXXX'
                  className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={validiFiBankVerification.accountNumber}
                  onChange={(e) => setValidiFiBankVerification((prevVerification) => ({
                    ...prevVerification,
                    accountNumber: e.target.value
                  }))}
                  required
                />
              </div>
              <div className='flex flex-col'>
                <label htmlFor='email' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Routing Number*
                </label>
                <input
                  type='text'
                  id='email'
                  placeholder='XXXXXXXXX'
                  className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={validiFiBankVerification.routingNumber}
                  onChange={(e) => setValidiFiBankVerification((prevVerification) => ({
                    ...prevVerification,
                    routingNumber: e.target.value
                  }))}
                  required
                />
              </div>
            </div>
            <div className='w-full flex flex-row items-center gap-2 max-sm:mb-10'>
              <button onClick={() => setStep(step - 1)} className='w-1/2 bg-gray-200 rounded-lg p-2'>
                Back
              </button>
              <button disabled={!validiFiBankVerification.bankName || !validiFiBankVerification.accountType || !validiFiBankVerification.accountNumber || !validiFiBankVerification.routingNumber || validiFiBankVerification.routingNumber.length !== 9 || !validiFiBankVerification.bankCity || !validiFiBankVerification.bankState || sendingValidation} onClick={handleSendBankInfo} className={`w-1/2 rounded-lg p-2 text-white ${validiFiBankVerification.bankName && validiFiBankVerification.accountType && validiFiBankVerification.accountNumber && validiFiBankVerification.routingNumber && validiFiBankVerification.routingNumber.length === 9 && validiFiBankVerification.bankCity && validiFiBankVerification.bankState && !sendingValidation ? 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200' : 'bg-gray-400 cursor-not-allowed'}`}>
                Continue
              </button>
            </div>
            {sendingValidation && (
              <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                <p>Requesting Bank Validation...</p>
              </div>
            )}
            {showError && (
              <p className='text-sm text-red-600'>Uh-oh! Something went wrong while sending your validation request. Please double-check your information and give it another try. We're here to help if you need assistance!</p>
            )}
          </div>
        ) : (
          <div className='w-full flex flex-col gap-4'>
            <p className='font-bold text-xl'>Your Bank Account Verification is Underway!</p>
            <p>We've partnered with ValidiFi to securely verify your bank account. Here's what is happening next:</p>
            <ul className='list-disc ml-5 mt-2'>
              <li>Watch for an email from ValidiFi - it's on its way!</li>
              <li>ValidiFi will make small deposits (less than $1) to your account.</li>
              <li>You'll receive simple instructions to confirm these deposits.</li>
              <li>Once you have validated your bank, you may continue the application process.</li>
            </ul>
            <p><span className='font-bold'>Estimated Deposit Date: </span>{estimatedDate.toLocaleDateString()}</p>
            <button onClick={() => { setReload(true); navigate('/') }} className='w-full rounded-lg p-2 text-white bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
              Return to Dashboard
            </button>
          </div>
        )
      ) : (
        <div className='flex flex-row items-center justify-center gap-2'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p>Loading...</p>
        </div>
      )}
    </div>
  )
}